import axios from "utils/axios";
import { API } from "constants/api";

// Types
import { AxiosResponse } from "axios";
import { Pagination } from "types/globals";
import qs from "qs";

//
// BEHAVIOURAL COMPARISON CHART
// -----------------------------------------------------------------------------
export interface BehaviouralComparisonResponse {
    graphs: BCGraph[];
}

export interface BCGraph {
    id: string;
    label: string;
    complete?: number;
    in_progress?: number;
    not_shared?: number;
    score_x?: { [key: string]: BCScoreX };
    data_series?: BCDataSeries;
}

export interface BCDataSeries {
    distribution_y?: BCDistributionY[];
    distributions?: { [key: string]: Array<number | null> };
    score_y?: BCScoreY[];
    scores?: { [key: string]: number[] };
}

export interface BCDistributionY {
    id: string;
}

export interface BCScoreY {
    "user.id": number;
    "user.name": string;
    "assessment.id": number;
}

export interface BCScoreX {
    id: string;
    synonym: string;
    antonym: string;
    min: number;
    max: number;
}

export const getBehaviouralComparisonData = (
    orgId: number,
    data = {}
): Promise<AxiosResponse<BehaviouralComparisonResponse>> =>
    axios.post(API.PACE_DASHBOARD.BEHAVIOURAL_COMPARISON(orgId), data);

//
// INDIVIDUALS REF
// -----------------------------------------------------------------------------
export interface BCIndividualsRefResponse {
    results: BCIndividualRef[];
    meta: Pagination;
}

export interface BCIndividualRef {
    id: number;
    name: string;
    teams: {
        name: string;
    }[];
}

export const getPaceDashboardIndividualsRef = (params: {
    orgId: number;
    filter_name?: string;
    filter_pace_only?: boolean;
    page_size?: number;
    page_no?: number;
}): Promise<AxiosResponse<BCIndividualsRefResponse>> => {
    const { orgId, ...metaParams } = params;
    return axios.get(
        `${API.PACE_DASHBOARD.INDIVIDUALS_REF(orgId)}?${qs.stringify(metaParams, { arrayFormat: "repeat" })}`
    );
};

//
// TEAMS REF
// -----------------------------------------------------------------------------
export interface BCTeamsRefResponse {
    results: BCTeamsRef[];
    meta: Pagination;
}

export interface TTeamUser {
    id: number;
    job_title: string;
    job_company_name: string;
    name: string;
}

export interface BCTeamsRef {
    name: string;
    count: number;
    users: TTeamUser[];
}

export const getPaceDashboardTeamsRef = (params: {
    orgId: number;
    filter_team?: string;
    page_size?: number;
    page_no?: number;
}): Promise<AxiosResponse<BCTeamsRefResponse>> => {
    const { orgId, ...metaParams } = params;
    return axios.get(`${API.PACE_DASHBOARD.TEAMS_REF(orgId)}?${qs.stringify(metaParams, { arrayFormat: "repeat" })}`);
};

//
// POST TEAM
// -----------------------------------------------------------------------------
export type BCTeamsRequestResponse = string;

export const createPaceDashboardTeams = (
    userIds: number[],
    teams: string[]
): Promise<AxiosResponse<BCTeamsRequestResponse>> => {
    return axios.post(API.PACE_DASHBOARD.USERS, {
        user_ids: userIds,
        teams: teams,
    });
};

export const updatePaceDashboardTeams = (
    userIds: number[],
    teams: string[]
): Promise<AxiosResponse<BCTeamsRequestResponse>> => {
    return axios.put(API.PACE_DASHBOARD.USERS, {
        user_ids: userIds,
        teams: teams,
    });
};

export const deletePaceDashboardUsersFromTeams = (
    userId: number,
    teams: string
): Promise<AxiosResponse<BCTeamsRequestResponse>> => {
    return axios.delete(API.PACE_DASHBOARD.TEAMS(userId), { data: { teams: [teams] } });
};
